import React, { useState } from 'react'
import { Check, Zap, Shield, Crown, Rocket, Star, Loader2 } from 'lucide-react'
import { useCheckout } from '../../hooks/useCheckout'
import { useTestVariant, useConversion } from '../../contexts/ABTestContext'

interface PricingTier {
  id: string
  name: string
  icon: React.ReactNode
  tagline: string
  monthlyPrice: number
  annualPrice: number
  credits: number
  features: string[]
  limitations?: string[]
  popular?: boolean
  ctaText: string
  pricePerCredit: number
}

interface PricingSectionProps {
  onSelectPlan: (planId: string, billingCycle: 'monthly' | 'annual') => void
}

export const PricingSection: React.FC<PricingSectionProps> = ({ onSelectPlan }) => {
  const pricingVariant = useTestVariant('pricing_display')
  const recordConversion = useConversion()
  
  // Set default billing cycle based on A/B test
  const defaultBillingCycle = pricingVariant === 'A' ? 'monthly' : 'annual'
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(defaultBillingCycle)
  const [processingPlan, setProcessingPlan] = useState<string | null>(null)
  const { initiateCheckout, loading, error } = useCheckout()

  const pricingTiers: PricingTier[] = [
    {
      id: 'free',
      name: 'Free Trial',
      icon: <Zap className="w-6 h-6" />,
      tagline: 'Perfect for testing the waters',
      monthlyPrice: 0,
      annualPrice: 0,
      credits: 100,
      pricePerCredit: 0,
      features: [
        '100 free credits to get started',
        'AI-powered metadata generation for photos & videos',
        'Basic SEO optimization',
        'Standard processing speed',
        'Email support',
        'All core features included'
      ],
      limitations: [
        'Limited to 100 files total',
        'Basic export formats only',
        'Standard processing speed'
      ],
      ctaText: 'Start Free Trial'
    },
    {
      id: 'starter',
      name: 'Starter',
      icon: <Star className="w-6 h-6" />,
      tagline: 'For hobby photographers',
      monthlyPrice: 6,
      annualPrice: 58, // ~17% discount
      credits: 500,
      pricePerCredit: 0.012,
      features: [
        '500 credits per month',
        'Advanced AI metadata generation for photos & videos',
        'SEO-optimized titles & descriptions',
        'Keyword suggestions (50+ per file)',
        'Batch processing up to 100 files',
        'CSV/JSON export',
        'Priority email support',
        'No watermarks'
      ],
      ctaText: 'Start Starter Plan'
    },
    {
      id: 'professional',
      name: 'Professional',
      icon: <Shield className="w-6 h-6" />,
      tagline: 'For serious photographers',
      monthlyPrice: 16,
      annualPrice: 154, // ~20% discount
      credits: 2000,
      pricePerCredit: 0.008,
      popular: true,
      features: [
        '2,000 credits per month',
        'Premium AI with 99.5% accuracy for photos & videos',
        'Advanced SEO optimization',
        'Stock platform optimization (Shutterstock, Adobe Stock, etc.)',
        'Unlimited batch processing',
        'All export formats',
        'Custom keyword templates',
        'Geolocation metadata',
        'Priority support (24h response)',
        'API access'
      ],
      ctaText: 'Go Professional'
    },
    {
      id: 'business',
      name: 'Business',
      icon: <Crown className="w-6 h-6" />,
      tagline: 'For agencies & teams',
      monthlyPrice: 50,
      annualPrice: 480, // ~20% discount
      credits: 10000,
      pricePerCredit: 0.005,
      features: [
        '10,000 credits per month',
        'Team collaboration tools for photos & videos',
        'Multiple user accounts (up to 5)',
        'Custom AI training on your style',
        'White-label export options',
        'Advanced analytics & reporting',
        'Custom metadata templates',
        'Webhook integrations',
        'Dedicated account manager',
        'Phone support'
      ],
      ctaText: 'Scale Your Business'
    }
  ]

  const getPrice = (tier: PricingTier) => {
    return billingCycle === 'monthly' ? tier.monthlyPrice : tier.annualPrice
  }

  const getSavings = (tier: PricingTier) => {
    if (tier.monthlyPrice === 0) return null
    const monthlyTotal = tier.monthlyPrice * 12
    const annualSavings = monthlyTotal - tier.annualPrice
    const savingsPercent = Math.round((annualSavings / monthlyTotal) * 100)
    return { amount: annualSavings, percent: savingsPercent }
  }

  const handleSelectPlan = async (planId: string, billingCycle: 'monthly' | 'annual') => {
    setProcessingPlan(planId)
    
    // Record conversion for plan selection
    recordConversion('pricing_display', 'plan_selected', {
      planId,
      billingCycle,
      defaultBillingCycle,
      variant: pricingVariant
    })
    
    try {
      const result = await initiateCheckout(planId, billingCycle)
      
      if (result.requiresSignup) {
        // For free trial, call the parent's onSelectPlan to trigger signup
        onSelectPlan(planId, billingCycle)
      }
      // If successful and doesn't require signup, user will be redirected to Stripe
    } catch (err) {
      console.error('Failed to initiate checkout:', err)
    } finally {
      setProcessingPlan(null)
    }
  }

  return (
    <section className="py-20 bg-gray-50" id="pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Choose Your Perfect Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Start free, then scale up as your needs grow. Every plan includes our core AI metadata generation.
          </p>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-md mx-auto">
              <div className="flex">
                <div className="text-red-800 text-sm">
                  <strong>Payment Error:</strong> {error}
                </div>
              </div>
            </div>
          )}

          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-white rounded-lg p-1 shadow-sm border">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 sm:px-6 py-3 rounded-md font-medium transition-all touch-manipulation ${
                billingCycle === 'monthly'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 sm:px-6 py-3 rounded-md font-medium transition-all relative touch-manipulation ${
                billingCycle === 'annual'
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Annual
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full animate-pulse">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {pricingTiers.map((tier) => {
            const price = getPrice(tier)
            const savings = getSavings(tier)
            const isPopular = tier.popular

            return (
              <div
                key={tier.id}
                className={`relative bg-white rounded-2xl shadow-lg border-2 transition-all duration-300 hover:shadow-xl transform hover:scale-105 ${
                  isPopular
                    ? 'border-blue-500 lg:scale-105'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
                style={{ animationDelay: `${pricingTiers.indexOf(tier) * 100}ms` }}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full text-sm font-bold">
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="p-6">
                  {/* Header */}
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg mb-4 ${
                      isPopular ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {tier.icon}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{tier.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{tier.tagline}</p>
                  </div>

                  {/* Pricing */}
                  <div className="text-center mb-6">
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">${price}</span>
                      {price > 0 && (
                        <span className="text-gray-600 ml-1">
                          /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                        </span>
                      )}
                    </div>
                    
                    {savings && billingCycle === 'annual' && (
                      <div className="text-green-600 text-sm font-medium mt-1">
                        Save ${savings.amount}/year ({savings.percent}% off)
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-600 mt-2">
                      {tier.credits.toLocaleString()} credits
                    </div>
                    
                    {tier.pricePerCredit > 0 && (
                      <div className="text-xs text-gray-500">
                        ${tier.pricePerCredit.toFixed(3)} per credit
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <div className="space-y-3 mb-6">
                    {tier.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5 mr-3" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                    
                    {tier.limitations && (
                      <div className="border-t pt-3 mt-3">
                        <div className="text-xs text-gray-500 mb-2">Limitations:</div>
                        {tier.limitations.map((limitation, index) => (
                          <div key={index} className="text-xs text-gray-500 mb-1">
                            • {limitation}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(tier.id, billingCycle)}
                    disabled={processingPlan === tier.id || loading}
                    className={`w-full py-3 px-4 rounded-lg font-semibold transition-all duration-200 flex items-center justify-center space-x-2 touch-manipulation active:scale-95 ${
                      processingPlan === tier.id
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : isPopular
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300'
                    }`}
                  >
                    {processingPlan === tier.id ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <span>{tier.ctaText}</span>
                    )}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="bg-blue-50 rounded-xl p-8 mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Pay-As-You-Go Credits Available
            </h3>
            <p className="text-gray-600 mb-6">
              Need extra credits? Purchase additional credits at $0.012 each (minimum 100 credits).
              Perfect for seasonal workflows or one-time projects.
            </p>
            <button
              onClick={() => handleSelectPlan('pay-as-go', 'monthly')}
              disabled={processingPlan === 'pay-as-go' || loading}
              className={`w-full sm:w-auto sm:mx-auto font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                processingPlan === 'pay-as-go'
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {processingPlan === 'pay-as-go' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </>
              ) : (
                <span>Buy Extra Credits</span>
              )}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">30-Day</div>
              <div className="text-gray-600">Money-back guarantee</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">99.2%</div>
              <div className="text-gray-600">Customer satisfaction</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600">Support available</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}